/**
 * Nova Derma — permission registry (SEC-01).
 *
 * Roles are fully user-buildable: a role is just a named set of these keys.
 * The presets below are seed data, not hard-coded behaviour — the admin can
 * change any of them from the UI.
 *
 * Key format: `<domain>.<resource>.<action>`
 * Actions: view | create | update | delete  (+ named actions for guarded operations)
 *
 * Frozen file — do not edit during feature work.
 */

export const PERMISSIONS = {
  // --- Patients (PAT-*) -----------------------------------------------------
  PATIENT_VIEW: 'patients.patient.view',
  PATIENT_CREATE: 'patients.patient.create',
  PATIENT_UPDATE: 'patients.patient.update',
  PATIENT_DELETE: 'patients.patient.delete',
  /** PAT-09 — merging two files moves appointments, sessions and invoices. */
  PATIENT_MERGE: 'patients.patient.merge',
  /** SEC-03 — the clinical record, hard-separated from personal data. */
  MEDICAL_RECORD_VIEW: 'patients.medicalRecord.view',
  MEDICAL_RECORD_UPDATE: 'patients.medicalRecord.update',
  /** PAT-25 — before/after album has its own permission: most sensitive asset. */
  PATIENT_PHOTO_VIEW: 'patients.photo.view',
  PATIENT_PHOTO_CREATE: 'patients.photo.create',
  PATIENT_PHOTO_DELETE: 'patients.photo.delete',

  // --- Consent (PAT-30..37) -------------------------------------------------
  CONSENT_FORM_VIEW: 'consents.form.view',
  CONSENT_FORM_MANAGE: 'consents.form.manage',
  CONSENT_SUBMISSION_VIEW: 'consents.submission.view',
  CONSENT_SUBMISSION_CREATE: 'consents.submission.create',
  /** PAT-34 — skipping a mandatory consent is a separate, logged permission. */
  CONSENT_OVERRIDE: 'consents.submission.override',

  // --- Appointments & calendar (APT-*, CAL-*) -------------------------------
  APPOINTMENT_VIEW: 'scheduling.appointment.view',
  APPOINTMENT_CREATE: 'scheduling.appointment.create',
  APPOINTMENT_UPDATE: 'scheduling.appointment.update',
  APPOINTMENT_DELETE: 'scheduling.appointment.delete',
  APPOINTMENT_CHECK_IN: 'scheduling.appointment.checkIn',
  APPOINTMENT_CHECK_OUT: 'scheduling.appointment.checkOut',
  /** APT-02 — double booking is allowed but the override is permissioned. */
  APPOINTMENT_DOUBLE_BOOK: 'scheduling.appointment.doubleBook',
  RECALL_MANAGE: 'scheduling.recall.manage',
  CALENDAR_SETTINGS_MANAGE: 'scheduling.calendarSettings.manage',
  PROVIDER_VIEW: 'scheduling.provider.view',
  PROVIDER_MANAGE: 'scheduling.provider.manage',
  PROVIDER_SCHEDULE_MANAGE: 'scheduling.providerSchedule.manage',

  // --- Clinical (CLIN-*) ----------------------------------------------------
  VISIT_VIEW: 'clinical.visit.view',
  VISIT_CREATE: 'clinical.visit.create',
  VISIT_UPDATE: 'clinical.visit.update',
  /** DOC-01 — the doctor's "today queue" screen. */
  DOCTOR_QUEUE_VIEW: 'clinical.doctorQueue.view',
  CLINICAL_TEMPLATE_MANAGE: 'clinical.template.manage',
  BODY_MAP_MANAGE: 'clinical.bodyMap.manage',

  // --- Pharmacy & labs (LAB-*) ---------------------------------------------
  PRESCRIPTION_VIEW: 'labs.prescription.view',
  PRESCRIPTION_CREATE: 'labs.prescription.create',
  LAB_ORDER_VIEW: 'labs.order.view',
  LAB_ORDER_CREATE: 'labs.order.create',
  LAB_RESULT_ENTER: 'labs.result.enter',
  MEDICATION_MANAGE: 'labs.medication.manage',
  LAB_TEST_MANAGE: 'labs.test.manage',

  // --- Inventory (INV-*) ----------------------------------------------------
  SUPPLIER_VIEW: 'inventory.supplier.view',
  SUPPLIER_MANAGE: 'inventory.supplier.manage',
  CATEGORY_VIEW: 'inventory.category.view',
  CATEGORY_MANAGE: 'inventory.category.manage',
  UNIT_MANAGE: 'inventory.unit.manage',
  BRAND_MANAGE: 'inventory.brand.manage',
  ATTRIBUTE_MANAGE: 'inventory.attribute.manage',
  ITEM_VIEW: 'inventory.item.view',
  ITEM_MANAGE: 'inventory.item.manage',
  PURCHASE_VIEW: 'inventory.purchase.view',
  PURCHASE_CREATE: 'inventory.purchase.create',
  /** INV-76 — posting is what moves stock. */
  PURCHASE_POST: 'inventory.purchase.post',
  /** INV-77 — only the admin may edit a posted invoice, and it is logged. */
  PURCHASE_EDIT_POSTED: 'inventory.purchase.editPosted',
  STOCK_MOVEMENT_VIEW: 'inventory.movement.view',
  STOCK_CONSUME: 'inventory.movement.consume',
  STOCK_WASTAGE: 'inventory.movement.wastage',
  STOCK_RETURN: 'inventory.movement.return',
  /** INV-90 — quick stocktake, no approval cycle. */
  STOCKTAKE_PERFORM: 'inventory.stocktake.perform',
  /** INV-92 — the weekly control report. */
  STOCKTAKE_REPORT_VIEW: 'inventory.stocktake.report',

  // --- Procedures & commission setup (COST-*) -------------------------------
  PROCEDURE_VIEW: 'catalog.procedure.view',
  PROCEDURE_MANAGE: 'catalog.procedure.manage',
  BOM_MANAGE: 'catalog.bom.manage',
  COMMISSION_RATE_VIEW: 'catalog.commissionRate.view',
  COMMISSION_RATE_MANAGE: 'catalog.commissionRate.manage',
  /** Packages — the admin-defined bundles of procedures sold at one price. */
  PACKAGE_VIEW: 'catalog.package.view',
  PACKAGE_MANAGE: 'catalog.package.manage',

  // --- Billing (BIL-*) ------------------------------------------------------
  INVOICE_VIEW: 'billing.invoice.view',
  INVOICE_CREATE: 'billing.invoice.create',
  INVOICE_UPDATE: 'billing.invoice.update',
  /** BIL-08 — void/refund always needs a mandatory reason. */
  INVOICE_VOID: 'billing.invoice.void',
  INVOICE_REFUND: 'billing.invoice.refund',
  PAYMENT_RECORD: 'billing.payment.record',
  /** BIL-06 — the ceiling itself is per-role, stored on the role. */
  DISCOUNT_APPLY: 'billing.discount.apply',
  PAYMENT_METHOD_MANAGE: 'billing.paymentMethod.manage',
  INSURANCE_VIEW: 'billing.insurance.view',
  INSURANCE_MANAGE: 'billing.insurance.manage',

  // --- Commissions & finance (COST-*) --------------------------------------
  /** COST-16 — a provider sees only their own statement. */
  COMMISSION_STATEMENT_VIEW_OWN: 'finance.commissionStatement.viewOwn',
  COMMISSION_STATEMENT_VIEW_ALL: 'finance.commissionStatement.viewAll',
  COMMISSION_STATEMENT_APPROVE: 'finance.commissionStatement.approve',
  COMMISSION_STATEMENT_PAY: 'finance.commissionStatement.pay',
  /** Q-B5-03 — open the source rows behind the caller's provider revenue KPIs. */
  DASHBOARD_REVENUE_VIEW_OWN: 'reports.financial.viewOwn',
  EXPENSE_VIEW: 'finance.expense.view',
  EXPENSE_MANAGE: 'finance.expense.manage',
  MATERIAL_COST_BEARER_MANAGE: 'finance.materialCostBearer.manage',
  PNL_VIEW: 'finance.pnl.view',

  // --- Messaging (MSG-*) ----------------------------------------------------
  STAFF_CHAT_DESK: 'staffChat.desk',
  MESSAGE_SEND: 'messaging.message.send',
  MESSAGE_LOG_VIEW: 'messaging.log.view',
  TEMPLATE_MANAGE: 'messaging.template.manage',
  CAMPAIGN_MANAGE: 'messaging.campaign.manage',
  SEGMENT_MANAGE: 'messaging.segment.manage',
  AGE_GROUP_MANAGE: 'messaging.ageGroup.manage',

  // --- Reports (RPT-*) ------------------------------------------------------
  REPORT_FINANCIAL: 'reports.financial.view',
  REPORT_OPERATIONAL: 'reports.operational.view',
  REPORT_PATIENTS: 'reports.patients.view',
  REPORT_CLINICAL: 'reports.clinical.view',
  REPORT_INVENTORY: 'reports.inventory.view',
  REPORT_COMMISSION: 'reports.commission.view',
  /** Admin-authored custom reports (report builder) — run a saved report. */
  REPORT_BUILDER_VIEW: 'reports.builder.view',
  /** Admin-authored custom reports — define, edit and delete a saved report. */
  REPORT_BUILDER_MANAGE: 'reports.builder.manage',

  // --- Administration (SEC-*) ----------------------------------------------
  USER_VIEW: 'admin.user.view',
  USER_MANAGE: 'admin.user.manage',
  ROLE_MANAGE: 'admin.role.manage',
  /** SEC-04 — changes only, never reads. */
  AUDIT_LOG_VIEW: 'admin.auditLog.view',
  SETTINGS_MANAGE: 'admin.settings.manage',
  REFERENCE_LIST_MANAGE: 'admin.referenceList.manage',
  ONLINE_BOOKING_MANAGE: 'booking.online.manage',
  ONLINE_BOOKING_VIEW: 'booking.online.view',
  ONLINE_BOOKING_REVIEW: 'booking.online.review',
  ONLINE_BOOKING_CONFIGURE: 'booking.online.configure',

  // --- HR, attendance & payroll ---------------------------------------------
  HR_VIEW: 'hr.employee.view',
  HR_MANAGE: 'hr.employee.manage',
  HR_CONFIGURE: 'hr.settings.configure',
  ATTENDANCE_SELF: 'hr.attendance.self',
  ATTENDANCE_OVERRIDE: 'hr.attendance.override',
  LEAVE_APPROVE: 'hr.leave.approve',
  PAYROLL_PREPARE: 'hr.payroll.prepare',
  PAYROLL_APPROVE: 'hr.payroll.approve',
  PAYROLL_PAY: 'hr.payroll.pay',
  PAYROLL_VIEW_SELF: 'hr.payroll.viewSelf',
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: PermissionKey[] = Object.values(PERMISSIONS);

/**
 * Grouping used to render the permission matrix screen (SEC-01).
 * Each group is one row-block; each key is a checkbox.
 */
export const PERMISSION_GROUPS: { key: string; labelAr: string; labelEn: string; permissions: PermissionKey[] }[] = [
  {
    key: 'patients',
    labelAr: 'المرضى',
    labelEn: 'Patients',
    permissions: [
      PERMISSIONS.PATIENT_VIEW,
      PERMISSIONS.PATIENT_CREATE,
      PERMISSIONS.PATIENT_UPDATE,
      PERMISSIONS.PATIENT_DELETE,
      PERMISSIONS.PATIENT_MERGE,
      PERMISSIONS.MEDICAL_RECORD_VIEW,
      PERMISSIONS.MEDICAL_RECORD_UPDATE,
      PERMISSIONS.PATIENT_PHOTO_VIEW,
      PERMISSIONS.PATIENT_PHOTO_CREATE,
      PERMISSIONS.PATIENT_PHOTO_DELETE,
    ],
  },
  {
    key: 'consents',
    labelAr: 'نماذج الموافقة',
    labelEn: 'Consent forms',
    permissions: [
      PERMISSIONS.CONSENT_FORM_VIEW,
      PERMISSIONS.CONSENT_FORM_MANAGE,
      PERMISSIONS.CONSENT_SUBMISSION_VIEW,
      PERMISSIONS.CONSENT_SUBMISSION_CREATE,
      PERMISSIONS.CONSENT_OVERRIDE,
    ],
  },
  {
    key: 'scheduling',
    labelAr: 'المواعيد والكالندر',
    labelEn: 'Appointments & calendar',
    permissions: [
      PERMISSIONS.APPOINTMENT_VIEW,
      PERMISSIONS.APPOINTMENT_CREATE,
      PERMISSIONS.APPOINTMENT_UPDATE,
      PERMISSIONS.ONLINE_BOOKING_VIEW,
      PERMISSIONS.ONLINE_BOOKING_REVIEW,
      PERMISSIONS.APPOINTMENT_DELETE,
      PERMISSIONS.APPOINTMENT_CHECK_IN,
      PERMISSIONS.APPOINTMENT_CHECK_OUT,
      PERMISSIONS.APPOINTMENT_DOUBLE_BOOK,
      PERMISSIONS.RECALL_MANAGE,
      PERMISSIONS.CALENDAR_SETTINGS_MANAGE,
      PERMISSIONS.PROVIDER_VIEW,
      PERMISSIONS.PROVIDER_MANAGE,
      PERMISSIONS.PROVIDER_SCHEDULE_MANAGE,
    ],
  },
  {
    key: 'clinical',
    labelAr: 'سير العمل السريري',
    labelEn: 'Clinical',
    permissions: [
      PERMISSIONS.VISIT_VIEW,
      PERMISSIONS.VISIT_CREATE,
      PERMISSIONS.VISIT_UPDATE,
      PERMISSIONS.DOCTOR_QUEUE_VIEW,
      PERMISSIONS.CLINICAL_TEMPLATE_MANAGE,
      PERMISSIONS.BODY_MAP_MANAGE,
    ],
  },
  {
    key: 'labs',
    labelAr: 'الأدوية والفحوصات',
    labelEn: 'Medications & labs',
    permissions: [
      PERMISSIONS.PRESCRIPTION_VIEW,
      PERMISSIONS.PRESCRIPTION_CREATE,
      PERMISSIONS.LAB_ORDER_VIEW,
      PERMISSIONS.LAB_ORDER_CREATE,
      PERMISSIONS.LAB_RESULT_ENTER,
      PERMISSIONS.MEDICATION_MANAGE,
      PERMISSIONS.LAB_TEST_MANAGE,
    ],
  },
  {
    key: 'inventory',
    labelAr: 'المخزون',
    labelEn: 'Inventory',
    permissions: [
      PERMISSIONS.SUPPLIER_VIEW,
      PERMISSIONS.SUPPLIER_MANAGE,
      PERMISSIONS.CATEGORY_VIEW,
      PERMISSIONS.CATEGORY_MANAGE,
      PERMISSIONS.UNIT_MANAGE,
      PERMISSIONS.BRAND_MANAGE,
      PERMISSIONS.ATTRIBUTE_MANAGE,
      PERMISSIONS.ITEM_VIEW,
      PERMISSIONS.ITEM_MANAGE,
      PERMISSIONS.PURCHASE_VIEW,
      PERMISSIONS.PURCHASE_CREATE,
      PERMISSIONS.PURCHASE_POST,
      PERMISSIONS.PURCHASE_EDIT_POSTED,
      PERMISSIONS.STOCK_MOVEMENT_VIEW,
      PERMISSIONS.STOCK_CONSUME,
      PERMISSIONS.STOCK_WASTAGE,
      PERMISSIONS.STOCK_RETURN,
      PERMISSIONS.STOCKTAKE_PERFORM,
      PERMISSIONS.STOCKTAKE_REPORT_VIEW,
    ],
  },
  {
    key: 'catalog',
    labelAr: 'الإجراءات والعمولات',
    labelEn: 'Procedures & commission rates',
    permissions: [
      PERMISSIONS.PROCEDURE_VIEW,
      PERMISSIONS.PROCEDURE_MANAGE,
      PERMISSIONS.BOM_MANAGE,
      PERMISSIONS.COMMISSION_RATE_VIEW,
      PERMISSIONS.COMMISSION_RATE_MANAGE,
      PERMISSIONS.PACKAGE_VIEW,
      PERMISSIONS.PACKAGE_MANAGE,
    ],
  },
  {
    key: 'billing',
    labelAr: 'الفوترة والدفع',
    labelEn: 'Billing',
    permissions: [
      PERMISSIONS.INVOICE_VIEW,
      PERMISSIONS.INVOICE_CREATE,
      PERMISSIONS.INVOICE_UPDATE,
      PERMISSIONS.INVOICE_VOID,
      PERMISSIONS.INVOICE_REFUND,
      PERMISSIONS.PAYMENT_RECORD,
      PERMISSIONS.DISCOUNT_APPLY,
      PERMISSIONS.PAYMENT_METHOD_MANAGE,
      PERMISSIONS.INSURANCE_VIEW,
      PERMISSIONS.INSURANCE_MANAGE,
    ],
  },
  {
    key: 'finance',
    labelAr: 'المالية والعمولات',
    labelEn: 'Finance & commissions',
    permissions: [
      PERMISSIONS.COMMISSION_STATEMENT_VIEW_OWN,
      PERMISSIONS.COMMISSION_STATEMENT_VIEW_ALL,
      PERMISSIONS.COMMISSION_STATEMENT_APPROVE,
      PERMISSIONS.COMMISSION_STATEMENT_PAY,
      PERMISSIONS.EXPENSE_VIEW,
      PERMISSIONS.EXPENSE_MANAGE,
      PERMISSIONS.PNL_VIEW,
      PERMISSIONS.MATERIAL_COST_BEARER_MANAGE,
    ],
  },
  {
    key: 'staffChat',
    labelAr: 'محادثة الفريق',
    labelEn: 'Staff chat',
    permissions: [PERMISSIONS.STAFF_CHAT_DESK],
  },
  {
    key: 'messaging',
    labelAr: 'الرسائل',
    labelEn: 'Messaging',
    permissions: [
      PERMISSIONS.MESSAGE_SEND,
      PERMISSIONS.MESSAGE_LOG_VIEW,
      PERMISSIONS.TEMPLATE_MANAGE,
      PERMISSIONS.CAMPAIGN_MANAGE,
      PERMISSIONS.SEGMENT_MANAGE,
      PERMISSIONS.AGE_GROUP_MANAGE,
    ],
  },
  {
    key: 'reports',
    labelAr: 'التقارير',
    labelEn: 'Reports',
    permissions: [
      PERMISSIONS.REPORT_FINANCIAL,
      PERMISSIONS.DASHBOARD_REVENUE_VIEW_OWN,
      PERMISSIONS.REPORT_OPERATIONAL,
      PERMISSIONS.REPORT_PATIENTS,
      PERMISSIONS.REPORT_CLINICAL,
      PERMISSIONS.REPORT_INVENTORY,
      PERMISSIONS.REPORT_COMMISSION,
      PERMISSIONS.REPORT_BUILDER_VIEW,
      PERMISSIONS.REPORT_BUILDER_MANAGE,
    ],
  },
  {
    key: 'admin',
    labelAr: 'الإدارة',
    labelEn: 'Administration',
    permissions: [
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.USER_MANAGE,
      PERMISSIONS.ROLE_MANAGE,
      PERMISSIONS.AUDIT_LOG_VIEW,
      PERMISSIONS.SETTINGS_MANAGE,
      PERMISSIONS.REFERENCE_LIST_MANAGE,
      PERMISSIONS.ONLINE_BOOKING_VIEW,
      PERMISSIONS.ONLINE_BOOKING_MANAGE,
      PERMISSIONS.ONLINE_BOOKING_REVIEW,
      PERMISSIONS.ONLINE_BOOKING_CONFIGURE,
    ],
  },
  {
    key: 'hr',
    labelAr: 'الموارد البشرية والحضور والرواتب',
    labelEn: 'HR, attendance & payroll',
    permissions: [
      PERMISSIONS.HR_VIEW,
      PERMISSIONS.HR_MANAGE,
      PERMISSIONS.HR_CONFIGURE,
      PERMISSIONS.ATTENDANCE_SELF,
      PERMISSIONS.ATTENDANCE_OVERRIDE,
      PERMISSIONS.LEAVE_APPROVE,
      PERMISSIONS.PAYROLL_PREPARE,
      PERMISSIONS.PAYROLL_APPROVE,
      PERMISSIONS.PAYROLL_PAY,
      PERMISSIONS.PAYROLL_VIEW_SELF,
    ],
  },
];

/**
 * Seed presets for the six roles in PRD §3. Editable from the UI afterwards.
 *
 * Note the two hard separations the PRD calls out:
 *  - Reception gets personal data only, never the medical record (SEC-03).
 *  - The specialist/technician gets VISIT_VIEW but is additionally constrained
 *    to her own procedure categories by the scope filter (SEC-02) — a permission
 *    key alone is not enough, see `procedureCategoryScope` on the role.
 */
export const ROLE_PERMISSION_PRESETS: Record<string, PermissionKey[]> = {
  ADMIN: ALL_PERMISSIONS,

  RECEPTIONIST: [
    PERMISSIONS.STAFF_CHAT_DESK,
    PERMISSIONS.PATIENT_VIEW,
    PERMISSIONS.PATIENT_CREATE,
    PERMISSIONS.PATIENT_UPDATE,
    PERMISSIONS.APPOINTMENT_VIEW,
    PERMISSIONS.APPOINTMENT_CREATE,
    PERMISSIONS.APPOINTMENT_UPDATE,
    PERMISSIONS.ONLINE_BOOKING_REVIEW,
    PERMISSIONS.ONLINE_BOOKING_VIEW,
    PERMISSIONS.APPOINTMENT_DELETE,
    PERMISSIONS.APPOINTMENT_CHECK_IN,
    PERMISSIONS.APPOINTMENT_CHECK_OUT,
    PERMISSIONS.APPOINTMENT_DOUBLE_BOOK,
    PERMISSIONS.RECALL_MANAGE,
    PERMISSIONS.PROVIDER_VIEW,
    PERMISSIONS.INVOICE_VIEW,
    PERMISSIONS.INVOICE_CREATE,
    PERMISSIONS.INVOICE_UPDATE,
    PERMISSIONS.PAYMENT_RECORD,
    PERMISSIONS.DISCOUNT_APPLY,
    PERMISSIONS.CONSENT_SUBMISSION_VIEW,
    PERMISSIONS.CONSENT_SUBMISSION_CREATE,
    PERMISSIONS.MESSAGE_SEND,
    PERMISSIONS.MESSAGE_LOG_VIEW,
    PERMISSIONS.PROCEDURE_VIEW,
    PERMISSIONS.PACKAGE_VIEW,
    PERMISSIONS.INSURANCE_VIEW,
  ],

  DOCTOR: [
    PERMISSIONS.PATIENT_VIEW,
    PERMISSIONS.PATIENT_CREATE,
    PERMISSIONS.PATIENT_UPDATE,
    PERMISSIONS.MEDICAL_RECORD_VIEW,
    PERMISSIONS.MEDICAL_RECORD_UPDATE,
    PERMISSIONS.PATIENT_PHOTO_VIEW,
    PERMISSIONS.PATIENT_PHOTO_CREATE,
    PERMISSIONS.APPOINTMENT_VIEW,
    PERMISSIONS.APPOINTMENT_CREATE,
    PERMISSIONS.APPOINTMENT_UPDATE,
    PERMISSIONS.DOCTOR_QUEUE_VIEW,
    PERMISSIONS.VISIT_VIEW,
    PERMISSIONS.VISIT_CREATE,
    PERMISSIONS.VISIT_UPDATE,
    PERMISSIONS.PRESCRIPTION_VIEW,
    PERMISSIONS.PRESCRIPTION_CREATE,
    PERMISSIONS.LAB_ORDER_VIEW,
    PERMISSIONS.LAB_ORDER_CREATE,
    PERMISSIONS.LAB_RESULT_ENTER,
    PERMISSIONS.CONSENT_FORM_VIEW,
    PERMISSIONS.CONSENT_SUBMISSION_VIEW,
    PERMISSIONS.CONSENT_SUBMISSION_CREATE,
    PERMISSIONS.STOCK_CONSUME,
    PERMISSIONS.ITEM_VIEW,
    PERMISSIONS.PROCEDURE_VIEW,
    PERMISSIONS.PACKAGE_VIEW,
    PERMISSIONS.DISCOUNT_APPLY,
    // COST-16: own statement only.
    PERMISSIONS.COMMISSION_STATEMENT_VIEW_OWN,
    PERMISSIONS.DASHBOARD_REVENUE_VIEW_OWN,
    PERMISSIONS.RECALL_MANAGE,
  ],

  SPECIALIST: [
    PERMISSIONS.PATIENT_VIEW,
    PERMISSIONS.APPOINTMENT_VIEW,
    PERMISSIONS.DOCTOR_QUEUE_VIEW,
    // SEC-02 narrows these to her own procedure categories.
    PERMISSIONS.VISIT_VIEW,
    PERMISSIONS.VISIT_CREATE,
    PERMISSIONS.VISIT_UPDATE,
    PERMISSIONS.PATIENT_PHOTO_VIEW,
    PERMISSIONS.PATIENT_PHOTO_CREATE,
    PERMISSIONS.CONSENT_SUBMISSION_VIEW,
    PERMISSIONS.CONSENT_SUBMISSION_CREATE,
    PERMISSIONS.STOCK_CONSUME,
    PERMISSIONS.ITEM_VIEW,
    PERMISSIONS.PROCEDURE_VIEW,
    PERMISSIONS.PACKAGE_VIEW,
    PERMISSIONS.COMMISSION_STATEMENT_VIEW_OWN,
    PERMISSIONS.DASHBOARD_REVENUE_VIEW_OWN,
  ],

  ACCOUNTANT: [
    PERMISSIONS.PATIENT_VIEW,
    PERMISSIONS.INSURANCE_VIEW,
    PERMISSIONS.INSURANCE_MANAGE,
    PERMISSIONS.INVOICE_VIEW,
    PERMISSIONS.PAYMENT_RECORD,
    PERMISSIONS.PAYMENT_METHOD_MANAGE,
    PERMISSIONS.EXPENSE_VIEW,
    PERMISSIONS.EXPENSE_MANAGE,
    PERMISSIONS.PNL_VIEW,
    PERMISSIONS.COMMISSION_STATEMENT_VIEW_ALL,
    PERMISSIONS.COMMISSION_STATEMENT_APPROVE,
    PERMISSIONS.COMMISSION_STATEMENT_PAY,
    PERMISSIONS.REPORT_FINANCIAL,
    PERMISSIONS.REPORT_OPERATIONAL,
    PERMISSIONS.REPORT_INVENTORY,
    PERMISSIONS.REPORT_COMMISSION,
    PERMISSIONS.REPORT_BUILDER_VIEW,
    PERMISSIONS.PURCHASE_VIEW,
    PERMISSIONS.SUPPLIER_VIEW,
  ],

  // SEC-06 / PRD §3 — defined for the future, no access today.
  DOCTOR_ASSISTANT: [],
};

/** BIL-06 — default discount ceilings per role, in percent. Editable per role. */
export const DEFAULT_DISCOUNT_CEILINGS: Record<string, number | null> = {
  ADMIN: null, // no limit
  RECEPTIONIST: 10,
  DOCTOR: 30,
  SPECIALIST: 10,
  ACCOUNTANT: 0,
  DOCTOR_ASSISTANT: 0,
};
